import React from "react"
import { Layout } from "../../components/Layout/Layout"
import { GetStaticProps, GetStaticPropsResult } from "next"
import { CourseAgeGroup } from "../../lib/prismic/Course"
import { Space, Typography } from "antd"
import { css } from "@emotion/react"
import { H2 } from "../../components/blog/common"
import { NextSeo } from "next-seo"
import { webUrl } from "../../config/web"
import { Section } from "../../components/Section"
import { SubscribeForm } from "../../components/SubscribeForm/SubscribeForm"
import { Bubbles } from "../../components/IndexHero/Bubbles"
import Container from "../../components/Layout/Container"
import { ShowWhenDesktop, ShowWhenMobile } from ".."
import { PageHero } from "../../components/IndexHero/PageHero"
import { ImageRounded } from "../organizace"
import Link from "next/link"
import { theme } from "../../styles/styles"
import { GroupedCourses } from "../../components/GroupedCourses/GroupedCourses"
import { AllProduct, getAllProducts, ProductsColorsItem } from "../../lib/contentful/product/getAllProducts"
import { EntryCollection } from "contentful"
import { TypeProductEntitySkeleton } from "../../lib/contentful/ContentfulVzdelanibudoucnostiTypes"
import { getAvailableProducts } from "../../lib/contentful/product/getAvailableProducts"
import { sm } from "../../styles/mediaQuery"

const dev = process.env.NODE_ENV !== "production"

const title = `Počítačové kroužky pro děti`
const description = `Programovat může každý! Tak proč by to nebavilo i Vašeho syna nebo Vaši dceru?`
const mappedBubbles = [
  {
    title: "Lepší budoucnost pro Vaše dítě.",
    text: "Náš vzdělávací systém je zaměřen na praktickou výuku programování, robotiky a 3D tisku. Děti učíme již od 9 let, a to zábavnou a hravou formou. Věříme, že na začátku cesty každého programátora má být motivace, podpora a nadšení. Naši studenti u nás získají potřebné know-how a pevné základy programování. \n"
  },
  {
    title: "Nabízíme skvělé kroužky i osobní doučování.",
    text: "Online výuka nám umožňuje učit Vaše dítě, ať jste kdekoliv. Během 12 lekcí vysvětlíme a společně projdeme všechna témata našich kroužků. 3D tisk a roboty posíláme po celé republice."
  },
  {
    title: "Jako jediní v republice nabízíme kurz formou stáže.",
    text: "Vaše dítě provedeme světem IT a společně vytvoříme projekty, kterými se bude moci prezentovat a v budoucnu se ucházet o brigádu nebo práci v IT sektoru."
  }
]

const KrouzkyProDeti = ({ contentfulProducts }: Props) => {
  const availableProductsAndCourses = getAvailableProducts(contentfulProducts)

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={`${webUrl}/krouzky-pro-deti`}
        openGraph={{
          url: `${webUrl}/krouzky-pro-deti`,
          title: title,
          description,
          images: [
            {
              url: `${webUrl}/images/header_laptop.jpg`,
              width: 1920,
              height: 1080,
              alt: "header_laptop"
            }
          ]
        }}
      />

      <Layout>
        <section>
          <PageHero
            h1={title}
            text={
              <>
                <Space size={20}>
                  <div
                    css={css`
                      width: 80px;
                      height: 80px;
                    `}>
                    <ImageRounded width={80} height={80} src="/images/organizace/jakub.jpg" alt="jakub" />
                  </div>

                  <Typography.Text
                    style={{ color: textWhite }}
                    css={css`
                      font-size: 18px;

                      ${sm} {
                        font-size: 16px;
                      }
                    `}>
                    Jmenuji se Jakub Valenta, jsem programátor a hrdý učitel Pythonu na základní škole a ve Vzdělání budoucnosti. Na učení mě nejvíc baví
                    objevovat s dětmi to, co baví je. Třeba programování v Minecraftu. To byste nevěřili, jak u toho dovedou být kreativní!
                  </Typography.Text>
                </Space>
              </>
            }>
            <ShowWhenDesktop>
              <Bubbles mappedBubbles={mappedBubbles} />
            </ShowWhenDesktop>
          </PageHero>
        </section>

        <Container>
          <Section>
            <ShowWhenMobile>
              <Bubbles mappedBubbles={mappedBubbles} />
            </ShowWhenMobile>
          </Section>

          <GroupedCourses
            courses={availableProductsAndCourses}
            productsColors={contentfulProducts?.productsColors || []}
            cssProp={css`
              padding-top: 0;
            `}
            moreInfo={true}
          />

          <Section>
            <H2 level={2}>Kurzy, které jsme v minulosti pořádali</H2>

            <Typography.Text>
              Máte zájem dozvědět se, které kurzy pro děti jsme v minulosti pořádali? <Link href="/poradali-jsme">Zde je jejich přehled</Link>.
            </Typography.Text>
          </Section>

          <Section>
            <H2 level={2}>Odběr novinek</H2>
            <SubscribeForm tags={["Krouzky", CourseAgeGroup.children, title]} />
          </Section>
        </Container>
      </Layout>
    </>
  )
}

const textWhite = theme.colors.dark.textWhite

interface Props {
  contentfulProducts: AllProduct
}

export const getStaticProps: GetStaticProps = async (): Promise<GetStaticPropsResult<Props>> => {
  const contentfulProducts = await getAllProducts({ "fields.subType[in]": ["course"] })

  return {
    props: {
      contentfulProducts: contentfulProducts || {
        products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
        productsColors: [] as ProductsColorsItem[]
      }
    },
    revalidate: dev ? 1 : 60
  }
}

export default KrouzkyProDeti
